/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    useRef, useEffect, useState,
} from 'react';
import {
    arrayOf, bool, func, string, object, shape,
} from 'prop-types';
import {
    makeStyles, useMediaQuery, SvgIcon, Box, IconButton,
} from '@material-ui/core';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff';
import NotificationsIcon from '@material-ui/icons/Notifications';
import {
    Minimize, Close, KeyboardArrowUp,
} from '@material-ui/icons';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withRouter } from 'react-router';
import { useUIDSeed } from 'react-uid';
import { connect } from 'react-redux';
import ChatInput from './ChatInput';
import ChatMessage from './ChatMessage';
import ConfirmDialog from './ConfirmDialog';
import MenuThreeDotDialog from './MenuThreeDotDialog';
import TimerDialog from './TimerDialog';
import EmailTranscriptInput from './EmailTranscriptInput';
import ResponsiveImage from '../../GraphqlComponents/GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { isCustomerServiceRoute } from '../utils/commonUtils';
import { getBrand } from '../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import brandLogo from './__tests__/__mocks__/brandLogo.json';
import ReconnectChatButton from './ReconnectChatButton';
import Feedback from './Feedback';
import { brandSurveyScript } from '../utils/qualtricsScripts';
import { isRichLink, redirectToShippingPage } from '../utils/AigoWidgetHelper';
import {
    EXTERNAL_TAB_KEYS,
    sendStatusToExternalTab,
} from '../utils/multipleTabsChat';

const typingIndicatorIcon = 'https://images.contentstack.io/v3/assets/bltf96456c406fb16e3/blt49ae5f2dcb9d2be7/60250ab33e70bb4c12c71bd9/typing-indicator.gif';
const chatIconDefault = 'https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/blt626a37e12c158340/6054b83ec4f5970f34583e7d/chatIcon-2.svg';

const useStyles = makeStyles((theme) => ({
    cjpChatPopup: {
        lineHeight: '1.6em',
        fontWeight: 400,
        fontFamily: 'Open Sans, sans-serif',
        display: 'none',
        position: 'fixed',
        bottom: 0,
        right: '0px',
        zIndex: 11,
        boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0,0,0,.12)',
        borderRadius: '5px 5px 0 0',
        [theme.breakpoints.up('1320')]: {
            fontSize: '16px',
        },
        [theme.breakpoints.between('1160', '1320')]: {
            fontSize: '16px',
        },
        [theme.breakpoints.down('1160')]: {
            fontSize: '14px',
        },
    },
    formContainer: {
        maxWidth: '100%',
        minWidth: 400,
        minHeight: 400,
        backgroundColor: 'white',
        position: 'relative',
        width: '100%',
        fallbacks: [
            {
                maxWidth: '400px !important',
            },
        ],
    },
    formContainerSM: {
        minWidth: 320,
    },
    formContainerMD: {
        minWidth: 400,
    },
    formTitle: {
        display: (props) => (props.chatWidgetMinimized ? 'none' : 'flex'),
        justifyContent: 'space-between',
        borderBottom: '1px solid #ddd',
        alignItems: 'center',
        padding: 11,
        background: theme.palette.cms?.primary,
        '& a': {
            color: '#8796a5',
        },
        '& div i': {
            fontSize: '1.2em',
            marginRight: 5,
        },
        '& div i.fa-comments': {
            color: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        },
    },
    commentTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    minimize: {
        minHeight: 'auto',
    },
    closeChat: {

    },
    chatContainer: {
        height: 390,
        overflow: 'auto',
        padding: '10px 15px',
        marginBottom: 15,
        overscrollBehavior: 'contain',
    },
    chatMessage: {
        '& > form': {
            display: 'flex',
            alignItems: 'center',
        },
        '& input[type=\'text\']': {
            flex: '1',
            padding: '12px 15px',
        },
        '& input[type=\'text\']:focus': {
            outline: 'none',
        },
        '& textarea': {
            flex: '1',
            backgroundColor: '#eee',
            padding: '5px 0 5px 20px',
            fontSize: '0.8rem',
        },
    },
    visible: {
        display: 'block',
        zIndex: '1001',
    },
    headerActionButton: {
        border: 0,
        backgroundColor: 'transparent',
        padding: 0,
        outline: 'none',
        cursor: 'pointer',
    },
    openButton: {
        zIndex: '999',
        position: 'fixed',
        bottom: 80,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        border: (props) => (props.isChatIconDisplayed || props.isFeedbackButtonDisplayed || props.showTopButton ? `solid ${theme.palette.cms?.primary} 1px` : 'none'),
        backgroundColor: 'white',
        alignItems: 'center',
        width: '70px',
        borderRight: 'none',
        '& svg': {
            color: theme.palette.cms?.primary,
            fontSize: '30px',
            width: 'auto',
            display: 'block',
            margin: '0 auto',
        },
        '& span': {
            display: 'block',
            color: theme.palette.cms?.primary,
            fontSize: '1rem',
            textAlign: 'center',
        },
        '& button': {
            padding: '10px 5px',
            color: theme.palette.cms?.primary,
            cursor: 'pointer',
            fontSize: '1.2rem',
            backgroundColor: 'white',
            // border: 'none',
            borderBottom: (props) => (!props.isChatIconDisplayed || !props.isFeedbackButtonDisplayed ? `solid ${theme.palette.cms?.primary} 1px` : ''),
            height: '70px',
            width: '100%',
            borderRadius: '0',
        },
        '& path': {
            fill: theme.palette.cms?.primary,
        },
        [theme.breakpoints.down('600')]: {
            right: 20,
        },
    },
    chatContainerMinimized: {
        display: 'none',
    },
    headerIcons: {
        color: '#fff',
    },
    headerIconClose: {
        position: 'absolute',
        right: 35,
        width: 20,
        bottom: 5,
    },
    headerIconOpen: {
        position: 'absolute',
        right: 30,
        top: 7,
        color: '#fff',
        fontSize: 25,
    },
    headerClose: {
        position: 'absolute',
        top: 15,
        right: 8,
        fontSize: 25,

    },
    chatInput: {
        fontSize: 14,
        border: '0.5px solid #ccc !important',
        borderRadius: '5px !important',
        margin: '10px !important',
        '& :focus': {
            fontSize: 'inherit !important',
        },
    },
    liveChatIcon: {
        width: 30,
        height: 25,
        marginRight: 5,
        filter: 'sepia(1) brightness(3)',
    },
    liveChatIconVC: {
        width: 30,
        height: 12,
        marginRight: 5,
    },
    liveChatIconHD: {
        width: 30,
        height: 25,
        margin: '-2px 0px ',
    },
    typingIndicator: {
        position: 'absolute',
        bottom: 95,
        overflowY: 'hidden',
        transitionProperty: 'all',
        transitionDuration: '.5s',
        transitionTimingFunction: 'cubic-bezier(0, 1, 0.5, 1)',
        width: 35,
        height: 20,
        left: 15,
    },
    agentTypingText: {
        position: 'absolute',
        bottom: 95,
        fontSize: 11,
        left: 50,
    },
    statusContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },
    statusText: {
        paddingTop: '15px',
    },
    statusContainerFail: {
        position: 'relative',
        top: '40%',
    },
    strokeClass: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 40,
        '&::after': {
            top: 10,
            width: '80%',
            content: 'no-close-quote',
            display: 'block',
            position: 'relative',
            borderBottom: 'solid 1px gray',
        },
    },
    statusTextFail: {
        margin: '0px auto',
        textAlign: 'center',
        position: 'absolute',
        zIndex: 99999,
        padding: '0 5px',
        background: 'white',
    },
    chatTitle: {
        color: '#fff',
        fontSize: '1.2em',
        margin: '5px -2px 0px',
    },
    chatHeaderHD: {
        color: '#fff',
        fontSize: '1.2em',
        margin: '5px 5px 0px',
    },
    threeDotSvg: {
        position: 'absolute',
        bottom: 0,
        right: 8,
        '& path': {
            fill: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        },
    },
    threeDotSvgDisabled: {
        pointerEvents: 'none',
        '& path': {
            fill: '#ccc',
        },
    },
    formFooter: {
        padding: '4%',
        '& $threeDotMenuContainer': {
            '&:hover': {
                '& $toolTipText': {
                    visibility: 'visible',
                },
            },
        },
    },
    toolTipText: {
        visibility: 'hidden',
        width: '80px',
        backgroundColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        color: '#fff',
        textAlign: 'center',
        borderRadius: '6px',
        padding: '5px 0',
        position: 'absolute',
        bottom: 26,
        right: 5,
        fontSize: 14,
        '&:after': {
            content: '""',
            position: 'absolute',
            top: '100%',
            left: '80%',
            marginLeft: '-5px',
            borderWidth: '5px',
            borderStyle: 'solid',
            borderColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary} transparent transparent transparent`,
        },
        border: 'none',
        cursor: 'pointer',
    },
    menuListContainer: {
        display: 'flex',
        bottom: 80,
        position: 'absolute',
        height: 160,
        overflowY: 'scroll',
        overflowX: 'auto',
        width: 350,
        zIndex: 12,
        boxShadow: '2px 2px 2px 2px grey',
        left: 15,
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.cms?.primary,
            borderRadius: 10,
        },
        '&::-webkit-scrollbar:vertical': {
            background: 'transparent',
            width: 8,
        },
        '&::-webkit-scrollbar:horizontal': {
            background: 'transparent',
            width: 8,
        },
    },
    addressList: {
        fontSize: 14,
        padding: '4px !important',
        fontWeight: '600',
        borderBottom: 'solid 1px #ecdede !important',
        display: 'block !important',
        'min-width': 350,
    },
    chatButton: {
        // Here to satisfy require prop on ResponsiveImage component
    },
    threeDotMenuContainer: {
        cursor: 'pointer',
    },
    modalBlocker: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        content: ' ',
        background: 'rgba(0, 0, 0, .5)',
        zIndex: 1,
    },
    feedBackButton: {
        borderTop: `solid ${theme.palette.cms?.primary} 1px !important`,
        borderBottom: `solid ${theme.palette.cms?.primary} 1px !important`,
        borderRadius: '0 !important',
        height: '70px !important',
    },
    feedBackButtonTop: {
        borderTop: 'none !important',
        backgroundColor: 'unset !important',
        height: '70px !important',
    },
    sendButton: {
        '&:not(.Mui-disabled)': {
            '& svg': {
                color: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
            },
        },
        '& .MuiTouchRipple-root span': {
            display: 'none !important',
        },
        '&:hover': {
            background: 'transparent !important',
        },
    },
    smartgiftEnabled: {
        [theme.breakpoints.down('600')]: {
            bottom: 95,
        },
    },
    textStyles: {
        fontSize: '12px !important',
        paddingTop: '8px !important',
    },
    '@keyframes glowing': {
        '0.8%': {
            backgroundColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
            boxShadow: `0 0 5px ${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        },
        '0.9%': {
            backgroundColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
            boxShadow: `0 0 5px ${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        },
    },
    minimizedColor: {
        animationName: '$glowing',
        animationDuration: '1300ms',
        animationIterationCount: 'infinite',
    },
    reversedColor: {
        backgroundColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary} !important`,
        color: '#FFFFFF !important',
    },
    feedBackColor: {
        color: 'white !important',
    },
    iconButton: {
        height: '70px !important',
    },
    notificationIcon: {
        paddingTop: '2px',
        position: 'absolute',
        right: '38px',
        bottom: '6px',
    },
    notificationIconColor: {
        color: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        '&:hover': {
            '& $notificationToolTipText': {
                visibility: 'visible',
            },
        },
    },
    notificationToolTipText: {
        visibility: 'hidden',
        width: '175px',
        backgroundColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        color: '#fff',
        textAlign: 'center',
        borderRadius: '6px',
        padding: '5px 0',
        position: 'absolute',
        bottom: 26,
        right: 10,
        fontSize: 14,
        '&:after': {
            content: '""',
            position: 'absolute',
            top: '100%',
            left: '80%',
            marginLeft: '-5px',
            borderWidth: '5px',
            borderStyle: 'solid',
            borderColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary} transparent transparent transparent`,
        },
        border: 'none',
        cursor: 'pointer',
    },
    privacyNotice: {
        fontSize: 12,
        padding: '5px 2px',
        bottom: '-2px',
        position: 'absolute',
        color: 'rgba(0, 0, 0, 0.80)',
    },
}));

const ChatWidgetView = ({
    chatWidgetMinimized,
    closeChatConfirm,
    showChatWidget,
    messages,
    onChatOpen,
    onCloseDialog,
    onCancelDialog,
    cancelEmailTranscript,
    onSubmitEmail,
    closeChatWidget,
    onSubmitMessage,
    minimizeChatWidget,
    startTimer,
    openEmailTranscriptInputDialog,
    closeChatAndCleanUpSession,
    showEmailTranscriptInput,
    emailSuccesMessage,
    showTimer,
    setShowTimer,
    setCloseChatConfirm,
    history,
    location,
    isLoading,
    errorState,
    setShowChatButton,
    featureFlags,
    isChatInputDisabled,
    brand,
    showThreeDotMenu,
    setShowThreeDotMenu,
    agentIsTyping,
    setIsTyping,
    botIsTyping,
    convId,
    showReconnectButton,
    setShowReconnectButton,
    reconnectChat,
    isBot,
    isAigoReady,
    forwardRef,
    callUpdateProfileEmailId,
    ffIsSmartgiftEnabled,
    handleSendSurveyLink,
    chatRef,
    feedbackMinimized,
    setFeedbackMinimized,
    feedbackRef,
    setIsFeedbackOpened,
    isFeedbackBtnClicked,
    isChatBtnClicked,
    isSoundNotificationOn,
    setShouldTurnNotificationOff,
    mainTabConvID,
    dummyTabId,
    setIsChatInputDisabled,
    isUseCaseTriggered,
    routing,
}) => {
    // states
    const [formContainerSize, setFormContainerSize] = useState(undefined);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [showTopButton, setShowTopButton] = useState(false);
    const [surveyFetched, setSurveyFetched] = useState(false);
    const [currentUrl, setCurrentUrl] = useState(window?.location?.href);
    // refs
    const chatContainerRef = useRef();
    const matches = useMediaQuery('(min-width: 576px)');
    const brandLogoImage = brandLogo.find((brandLog) => (brandLog.shortName === brand.code));
    const brandLogoChat = brandLogoImage?.url ? brandLogoImage?.url : chatIconDefault;
    const lastMessageIndex = messages?.[messages?.length - 1];
    const isChatIconDisplayed = featureFlags['is-chat-icon-displayed'];
    const isFeedbackButtonDisplayed = featureFlags['is-feedback-button-displayed'];
    const classes = useStyles({
        showTopButton, isFeedbackButtonDisplayed, isChatIconDisplayed, chatWidgetMinimized,
    });
    /**
     * Scrolls to bottom of conversation every time a new message is sent
     */
    const scrollToBottom = () => {
        const chatContainerDiv = chatContainerRef.current;
        if (chatContainerDiv) {
            chatContainerDiv.scrollTop = chatContainerDiv.scrollHeight;
        }
    };

    /**
     * Handles click on 3 dots
     */
    const handleClick = () => {
        setShowThreeDotMenu(!showThreeDotMenu);
    };

    const seed = useUIDSeed();

    /**
     * This is responsible for rendering either a section that is loading while the websocket is connecting,
     * an error if the websocket was unable to connect,
     * or the messages array in the chat if all is good in paradise.
     */
    const showLoadingOrErrorOrMessages = () => {
        if (isLoading && !errorState?.error) {
            return (
                <div className={classes.statusContainer}>
                    <p className={classes.statusText}>Connecting...</p>
                    <br />
                    <CircularProgress size={24} />
                </div>
            );
        } if (errorState?.error) {
            return (
                <div className={classes.statusContainerFail}>
                    <div className={classes.strokeClass}>
                        <p className={classes.statusTextFail}>
                            {errorState.errorMessage}
                        </p>
                    </div>
                    <ReconnectChatButton
                        reconnectChat={reconnectChat}
                        isBot={isBot}
                        isAigoReady={isAigoReady}
                        brand={brand}
                    />
                </div>
            );
        }
        return (
            <div> {messages && messages.map((message) => (
                <ChatMessage
                    key={seed(message)}
                    onSubmitMessage={onSubmitMessage}
                    messageObj={message}
                    isRichLink={isRichLink}
                    featureFlags={featureFlags}
                    lastMessageIndex={lastMessageIndex}
                    brand={brand}
                    convId={convId}
                />
            ))}
            </div>
        );
    };
    const brandLogoChatClassName = (brandCode) => {
        const logoChatclases = {
            HD: classes.liveChatIconHD,
            VC: classes.liveChatIconVC,
        };
        return logoChatclases[brandCode] || classes.liveChatIcon;
    };
    /**
     * Scrolls to bottom of conversation every time a new message is sent
     */
    useEffect(() => {
        if ((messages && messages.length)) {
            scrollToBottom();
        }
    }, [messages]);

    /**
     * Scrolls to bottom of conversation if dummyTab chat opened
     */
    useEffect(() => {
        if (dummyTabId && !isLoading) scrollToBottom();
    }, [isLoading, dummyTabId]);

    /**
     * If last message is a rich link with ProcessExpressRequest, load it in current window
     */
    useEffect(() => {
        if (messages?.length && isRichLink(messages[messages.length - 1], convId)) {
            redirectToShippingPage(isRichLink(messages[messages.length - 1], convId).url, location, convId, history, callUpdateProfileEmailId);
        }
    }, [lastMessageIndex]);

    useEffect(() => {
        if (isCustomerServiceRoute(location) && !featureFlags['is-show-aigo-on-customer-service-page-enabled']) {
            setShowChatButton(false);
        } else if (!showChatWidget) {
            setShowChatButton(true);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (matches) {
            setFormContainerSize(classes.formContainerMD);
        } else {
            setFormContainerSize(classes.formContainerSM);
        }
    }, [matches]);

    useEffect(() => {
        let lastScrollTop = 0;
        window.addEventListener('scroll', () => {
            if (window.scrollY > (document.body.scrollHeight / 2)) {
                setShowTopButton(true);
            } else if (window.pageYOffset < lastScrollTop) {
                setShowTopButton(false);
            }
            lastScrollTop = window.pageYOffset <= 0 ? 0 : window.pageYOffset;
        });
    }, []);

    useEffect(() => {
        if (window?.location?.href !== currentUrl) {
            setShowTopButton(false);
            setCurrentUrl(window?.location?.href);
        }
    }, [window?.location?.href]);

    /* This useEffect is used to trigger Feedback and Chat Button onClick functionality on first load */
    useEffect(() => {
        if (isFeedbackBtnClicked) {
            if (!feedbackOpen) {
                setFeedbackOpen(!feedbackOpen);
                setIsFeedbackOpened(true);
            }
            if (!surveyFetched) {
                brandSurveyScript(brand.code);
                setSurveyFetched(true);
            }
            if (feedbackMinimized) {
                setFeedbackMinimized(!feedbackMinimized);
            }
        }
        if (isChatBtnClicked && !convId && !dummyTabId) {
            onChatOpen(isUseCaseTriggered);
            setFeedbackOpen(false);
        }
    }, []);

    return (
        <>
            <Feedback
                open={feedbackOpen}
                setFeedbackOpen={setFeedbackOpen}
                feedbackMinimized={feedbackMinimized}
                setFeedbackMinimized={setFeedbackMinimized}
                setSurveyFetched={setSurveyFetched}
                feedbackRef={feedbackRef}
                setIsFeedbackOpened={setIsFeedbackOpened}
            />
            <div data-testid="ChatWidgetWrapper" ref={chatRef}>
                <div className={` ${ffIsSmartgiftEnabled ? classes.smartGiftEnabled : ''} ${classes.openButton}`}>
                    {showTopButton
                        && (
                            <IconButton
                                aria-hidden="true"
                                tabIndex="-1"
                                onClick={() => { window.scrollTo(0, 0); setShowTopButton(!showTopButton); }}
                                className={classes.iconButton}
                            >
                                <KeyboardArrowUp style={{
                                    position: 'relative', bottom: '0px', padding: '0px', margin: '0px, 0px, 0px, 15px',
                                }}
                                />
                                <span className={classes.textStyles}>Top</span>
                            </IconButton>
                        )}
                    {isFeedbackButtonDisplayed
                        && (
                            <IconButton
                                aria-hidden="true"
                                tabIndex="-1"
                                // eslint-disable-next-line
                                className={` ${feedbackMinimized ? classes.reversedColor : classes.feedBackButton} ${!showTopButton ? classes.feedBackButtonTop : ''}`}
                                onClick={() => {
                                    if (!feedbackOpen) {
                                        setFeedbackOpen(!feedbackOpen);
                                        setIsFeedbackOpened(true);
                                    }
                                    if (!surveyFetched) {
                                        brandSurveyScript(brand.code);
                                        setSurveyFetched(true);
                                    }
                                    if (feedbackMinimized) {
                                        setFeedbackMinimized(!feedbackMinimized);
                                    }
                                }}
                            >
                                <span className={feedbackMinimized ? classes.feedBackColor : ''}>[+]</span>
                                <span className={`${classes.textStyles} ${feedbackMinimized ? classes.feedBackColor : ''}`}>Feedback</span>
                                {/* <span id="qualtrics-feedback-button-label" /> */}
                            </IconButton>
                        )}
                    {isChatIconDisplayed
                        && (
                            <IconButton
                                aria-hidden="true"
                                tabIndex="-1"
                                data-testid="open-chat-btn"
                                onClick={() => {
                                    onChatOpen();
                                    setFeedbackOpen(false);
                                }}
                                className={`${classes.iconButton} ${chatWidgetMinimized ? classes.minimizedColor : ''}`}
                            >
                                <SvgIcon>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 48.23 41.81" fill="#fff">
                                        <path
                                            d="M17.83 40.78c5.75-.11 9.34-2.94 11.45-7.91.49-1.15 2.13-.61 3.25-.79 8.34-1.3 14.08-8 13.52-15.88-.56-8-6.93-13.83-15.44-14-4-.09-8-.05-12 0C9.51 2.25 3 7.62 2.21 15.73s4.7 14.66 13.53 16.35c.8.15 1.69 0 2.4 1-1.42 2.14-3.52 1.09-5.15.69a17.47 17.47 0 0 1-12.87-19C1 7 8.43.3 16.59.09q7.5-.18 15 0c8.25.21 15.63 6.85 16.53 14.69 1 8.65-4.41 17-12.85 18.91a10 10 0 0 0-7 4.52c-2.42 3.71-5.87 4.52-10.44 2.57zM36 13.72H12.24v-1.06H36zm0 8.09H12.26v-1.06H36z"
                                        />
                                    </svg>
                                </SvgIcon>
                                <span className={classes.textStyles}>Chat</span>
                            </IconButton>
                        )}
                </div>
                <div id="chat-widget-container" className={`${classes.cjpChatPopup} ${showChatWidget ? classes.visible : ''}`}>
                    <div id="myForm">
                        <ConfirmDialog
                            closeChatConfirm={closeChatConfirm}
                            onCloseDialog={onCloseDialog}
                            onCancelDialog={onCancelDialog}
                        />
                        <MenuThreeDotDialog
                            showThreeDotMenu={showThreeDotMenu}
                            openEmailTranscriptInputDialog={openEmailTranscriptInputDialog}
                            closeChatAndCleanUpSession={closeChatAndCleanUpSession}
                            chatWidgetMinimized={chatWidgetMinimized}
                            featureFlags={featureFlags}
                            convId={convId}
                            mainTabConvID={mainTabConvID}
                            dummyTabId={dummyTabId}
                        />
                        <TimerDialog
                            startTimer={startTimer}
                            messages={messages}
                            closeChatAndCleanUpSession={closeChatAndCleanUpSession}
                            showTimer={showTimer}
                            setShowTimer={setShowTimer}
                            onCloseDialog={onCloseDialog}
                            setCloseChatConfirm={setCloseChatConfirm}
                            setShowReconnectButton={setShowReconnectButton}
                            chatWidgetMinimized={chatWidgetMinimized}
                            minimizeChatWidget={minimizeChatWidget}
                            handleSendSurveyLink={handleSendSurveyLink}
                            convId={convId}
                            mainTabConvID={mainTabConvID}
                            dummyTabId={dummyTabId}
                            featureFlags={featureFlags}
                            setIsChatInputDisabled={setIsChatInputDisabled}
                        />
                        <EmailTranscriptInput
                            onSubmitEmail={onSubmitEmail}
                            showEmailTranscriptInput={showEmailTranscriptInput}
                            cancelEmailTranscript={cancelEmailTranscript}
                            emailSuccesMessage={emailSuccesMessage}
                        />

                        <div data-testid="chat-bot-screen" className={`${classes.formContainer} ${chatWidgetMinimized ? classes.minimize : ''} ${formContainerSize}`}>

                            <Box
                                className={classes.formTitle}
                                data-testid="chat-header"
                                onClick={() => {
                                    if (chatWidgetMinimized) minimizeChatWidget();
                                }}
                            >
                                <Box flexGrow={2}>
                                    <object
                                        data={brandLogoChat}
                                        title={brandLogoChat}
                                        type="image/svg+xml"
                                        aria-label="svg"
                                        className={brandLogoChatClassName(brand?.code)}
                                        params={{ desktop: 'quality=100', mobile: 'quality=100' }}
                                    />
                                    <span className={brand?.code === 'HD' ? classes.chatHeaderHD : classes.chatTitle}>
                                        Chat with us
                                    </span>
                                </Box>
                                <Box>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            minimizeChatWidget();
                                        }}
                                        className={`${classes.headerActionButton}`}
                                        data-testid="minimize-maximize"
                                        disabled={showTimer}
                                    >
                                        <Minimize fontSize="small" data-testid="minimize" className={classes.headerIcons} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={closeChatWidget}
                                        className={`${classes.headerActionButton}`}
                                        data-testid="close-button"
                                        disabled={showTimer}
                                    >
                                        <Close fontSize="small" className={classes.headerIcons} />
                                    </button>
                                </Box>
                            </Box>
                            <div className={classes.formContent}>
                                {showThreeDotMenu && <Box className={classes.modalBlocker} onClick={() => setShowThreeDotMenu(false)} />}
                                <div
                                    ref={chatContainerRef}
                                    className={`${classes.chatContainer} ${chatWidgetMinimized ? classes.chatContainerMinimized : ''}`}
                                    id="chatcontainer"
                                >
                                    {showLoadingOrErrorOrMessages()}
                                </div>

                                {
                                    agentIsTyping && (
                                        <>
                                            <ResponsiveImage
                                                className={chatWidgetMinimized ? classes.chatContainerMinimized : classes.typingIndicator}
                                                alt="typing indicator"
                                                path={typingIndicatorIcon}
                                                params={{ desktop: 'quality=100', mobile: 'quality=100' }}
                                            />
                                            <span className={chatWidgetMinimized ? classes.chatContainerMinimized : classes.agentTypingText}> Agent is typing </span>
                                        </>
                                    )
                                }

                                {
                                    botIsTyping && (
                                        <>
                                            <ResponsiveImage
                                                className={chatWidgetMinimized ? classes.chatContainerMinimized : classes.typingIndicator}
                                                alt="typing indicator"
                                                path={typingIndicatorIcon}
                                                params={{ desktop: 'quality=100', mobile: 'quality=100' }}
                                            />
                                        </>
                                    )
                                }
                            </div>
                            <div className={`${classes.chatMessage} ${chatWidgetMinimized ? classes.chatContainerMinimized : ''}`}>
                                {!isLoading && !errorState?.error && !showReconnectButton && (
                                    <ChatInput
                                        onSubmitMessage={onSubmitMessage}
                                        classList={{
                                            chatInput: classes.chatInput,
                                            menuListContainer: classes.menuListContainer,
                                            addressList: classes.addressList,
                                        }}
                                        forwardRef={forwardRef}
                                        isChatInputDisabled={isChatInputDisabled}
                                        setIsTyping={setIsTyping}
                                        lastMessageIndex={lastMessageIndex}
                                        featureFlags={featureFlags}
                                        chatWidgetMinimized={chatWidgetMinimized}
                                        convId={convId}
                                    />
                                )}
                                {!isLoading && showReconnectButton && !errorState?.error && (
                                    <ReconnectChatButton
                                        reconnectChat={reconnectChat}
                                        isBot={isBot}
                                        isAigoReady={isAigoReady}
                                        brand={brand}
                                    />
                                )}
                            </div>
                            <div className={`${classes.formFooter} ${chatWidgetMinimized ? classes.chatContainerMinimized : ''}`}>
                                <div className={`${showReconnectButton ? classes.threeDotSvgDisabled : ''}`} data-testid="three-dot-container">
                                    <div className={classes.threeDotMenuContainer}>
                                        <SvgIcon className={classes.threeDotSvg} onClick={() => handleClick()} data-testid="three-dot">
                                            <path d="M10.001 7.8a2.2 2.2 0 1 0 0 4.402A2.2 2.2 0 0 0 10 7.8zm-7 0a2.2 2.2 0 1 0 0 4.402A2.2 2.2 0 0 0 3 7.8zm14 0a2.2 2.2 0 1 0 0 4.402A2.2 2.2 0 0 0 17 7.8z" />
                                        </SvgIcon>
                                        <button type="button" className={classes.toolTipText} onClick={() => handleClick()}>Options</button>
                                    </div>
                                    <div className={`${classes.notificationIconColor}`}>
                                        {!isSoundNotificationOn ? (
                                            <>
                                                <NotificationsOffIcon
                                                    className={`${classes.notificationIcon} ${classes.threeDotMenuContainer}`}
                                                    fontSize="small"
                                                    onClick={() => { setShouldTurnNotificationOff(!isSoundNotificationOn); sendStatusToExternalTab(EXTERNAL_TAB_KEYS.ON_NOTIFICATION_SOUND, mainTabConvID, convId, dummyTabId, !isSoundNotificationOn, featureFlags); }}
                                                />
                                                <button type="button" className={classes.notificationToolTipText} onClick={() => { setShouldTurnNotificationOff(!isSoundNotificationOn); }}>Turn sound notification on</button>
                                            </>
                                        ) : (
                                            <>
                                                <NotificationsIcon
                                                    className={`${classes.notificationIcon} ${classes.threeDotMenuContainer}`}
                                                    fontSize="small"
                                                    onClick={() => { setShouldTurnNotificationOff(!isSoundNotificationOn); sendStatusToExternalTab(EXTERNAL_TAB_KEYS.ON_NOTIFICATION_SOUND, mainTabConvID, convId, dummyTabId, !isSoundNotificationOn, featureFlags); }}
                                                />
                                                <button type="button" className={classes.notificationToolTipText} onClick={() => { setShouldTurnNotificationOff(!isSoundNotificationOn); }}>Turn sound notification off</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <a
                                        className={`${classes.privacyNotice}`}
                                        href={`https://${window?.location?.hostname}/About-Us-Privacy-Policy`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >Privacy Notice
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    ffIsSmartgiftEnabled: getFeatureFlag('is-smart-gift-enabled')(state),
});
ChatWidgetView.propTypes = {
    messages: arrayOf(shape({
        userName: string,
        responseMessage: object,
        msgType: string.isRequired,
    })).isRequired,
    onChatOpen: func.isRequired,
    setIsTyping: func.isRequired,
    onCloseDialog: func.isRequired,
    onSubmitMessage: func.isRequired,
    onCancelDialog: func.isRequired,
    closeChatWidget: func.isRequired,
    cancelEmailTranscript: func,
    showChatWidget: bool.isRequired,
    minimizeChatWidget: func.isRequired,
    chatWidgetMinimized: bool.isRequired,
    closeChatConfirm: bool,
    startTimer: bool.isRequired,
    openEmailTranscriptInputDialog: func,
    closeChatAndCleanUpSession: func.isRequired,
    onSubmitEmail: func,
    showTimer: bool,
    setShowTimer: func.isRequired,
    setCloseChatConfirm: func.isRequired,
    setShowChatButton: func.isRequired,
    history: shape({
        push: func,
    }).isRequired,
    brand: object.isRequired,
    location: shape({
        pathname: string,
    }).isRequired,
    isLoading: bool.isRequired,
    showThreeDotMenu: bool.isRequired,
    emailSuccesMessage: bool,
    showEmailTranscriptInput: bool,
    setShowThreeDotMenu: func.isRequired,
    errorState: shape({
        error: bool,
        errorMessage: string,
    }).isRequired,
    featureFlags: object.isRequired,
    isChatInputDisabled: bool.isRequired,
    agentIsTyping: bool.isRequired,
    botIsTyping: bool,
    convId: string,
    showReconnectButton: bool.isRequired,
    setShowReconnectButton: func,
    reconnectChat: func.isRequired,
    isBot: bool.isRequired,
    isAigoReady: bool.isRequired,
    forwardRef: object.isRequired,
    callUpdateProfileEmailId: func.isRequired,
    ffIsSmartgiftEnabled: bool.isRequired,
    handleSendSurveyLink: func.isRequired,
    chatRef: object.isRequired,
    feedbackMinimized: bool.isRequired,
    setFeedbackMinimized: func.isRequired,
    feedbackRef: bool.isRequired,
    setIsFeedbackOpened: func.isRequired,
    isFeedbackBtnClicked: bool.isRequired,
    isChatBtnClicked: bool.isRequired,
    isSoundNotificationOn: bool.isRequired,
    setShouldTurnNotificationOff: func.isRequired,
    setIsChatInputDisabled: func.isRequired,
    mainTabConvID: string,
    dummyTabId: string,
    isUseCaseTriggered: string,
    routing: string,
};

ChatWidgetView.defaultProps = {
    showEmailTranscriptInput: false,
    closeChatConfirm: false,
    emailSuccesMessage: false,
    botIsTyping: false,
    showTimer: false,
    convId: '',
    onSubmitEmail: () => { },
    openEmailTranscriptInputDialog: () => { },
    cancelEmailTranscript: () => { },
    setShowReconnectButton: () => { },
    mainTabConvID: '',
    dummyTabId: '',
    isUseCaseTriggered: '',
    routing: 'toAigo',
};

export default connect(mapStateToProps)(withRouter(ChatWidgetView));
